// Spiegelt trainerstunden_trainers nach club_people (gleiche id). SSOT: Trainerstunden-Stammdaten.

import { getSupabaseClient } from "../supabase/index.js";
import { pickPostalAddressFields } from "./postal-address.js";
import { createClubPerson, updateClubPerson } from "./people.js";
import {
  findClubPeopleByNormalizedNameInTenant,
  normalizeClubPersonNamePart,
} from "./club-person-name-match.js";

export const CLUB_PERSON_CONTACT_SOURCE_TRAINER = "trainer";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __lowerEmail(v) {
  const s = __asStr(v).toLowerCase();
  return s || "";
}

/**
 * @param {object} trainerRow
 * @returns {Record<string, unknown>}
 */
export function buildClubPersonPatchFromTrainer(trainerRow) {
  const id = __asStr(trainerRow?.id);
  return {
    tenant_id: trainerRow?.tenant_id ?? null,
    first_name: trainerRow?.first_name ?? null,
    last_name: trainerRow?.last_name ?? null,
    email: __asStr(trainerRow?.login_email) || null,
    phone: null,
    notes: trainerRow?.notes ?? null,
    invoice_recipient_type: "self",
    billing_contact_id: null,
    relation_to_contact: null,
    billing_frequency: null,
    billing_payment_timing: null,
    is_inactive: Boolean(trainerRow?.is_inactive),
    inactive_at: trainerRow?.inactive_at ?? null,
    birth_year: null,
    contact_source: CLUB_PERSON_CONTACT_SOURCE_TRAINER,
    ...pickPostalAddressFields(trainerRow),
  };
}

/**
 * Sucht einen nativen Spieler-Kontakt (E-Mail, sonst Vor-/Nachname im Tenant).
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} trainerId
 * @param {{ loginEmail?: string, firstName?: string, lastName?: string, tenantId?: string }} ctx
 */
async function __resolveLinkedPlayerPersonId(supabase, trainerId, ctx = {}) {
  const email = __lowerEmail(ctx?.loginEmail);
  if (email) {
    const { data, error } = await supabase
      .from("club_people")
      .select("id, contact_source")
      .is("contact_source", null)
      .ilike("email", email)
      .neq("id", trainerId)
      .limit(1)
      .maybeSingle();
    if (!error && data?.id) return __asStr(data.id) || null;
  }

  const tenantId = __asStr(ctx?.tenantId);
  const firstName = ctx?.firstName;
  const lastName = ctx?.lastName;
  if (
    !tenantId ||
    !normalizeClubPersonNamePart(firstName) ||
    !normalizeClubPersonNamePart(lastName)
  ) {
    return null;
  }

  const matches = await findClubPeopleByNormalizedNameInTenant(
    supabase,
    tenantId,
    firstName,
    lastName,
    { excludeIds: [trainerId] },
  );
  const native = matches.filter((row) => !__asStr(row?.contact_source));
  if (native.length === 1) return __asStr(native[0]?.id) || null;
  return null;
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} playerPersonId
 */
export async function findTrainerIdForLinkedPlayerPersonId(
  supabase,
  playerPersonId,
) {
  const pid = __asStr(playerPersonId);
  if (!supabase || !pid) return "";
  const { data, error } = await supabase
    .from("club_people")
    .select("id")
    .eq("contact_source", CLUB_PERSON_CONTACT_SOURCE_TRAINER)
    .eq("linked_player_person_id", pid)
    .eq("is_inactive", false)
    .limit(1)
    .maybeSingle();
  if (error || !data?.id) return "";
  return __asStr(data.id);
}

/**
 * Legt bzw. aktualisiert club_people mit id = trainer.id (Mirror für Kontaktliste).
 *
 * @param {object} trainerRow Felder wie trainerstunden_trainers (mind. id)
 * @returns {Promise<{ ok: boolean, error?: unknown }>}
 */
export async function upsertClubPersonMirrorFromTrainer(trainerRow) {
  const id = __asStr(trainerRow?.id);
  if (!id) return { ok: false, error: "missing-id" };
  if (Boolean(trainerRow?.is_free_training)) return { ok: true, skipped: true };

  const patch = buildClubPersonPatchFromTrainer(trainerRow);

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const linkedPlayerId =
      __asStr(trainerRow?.linked_player_person_id) ||
      (await __resolveLinkedPlayerPersonId(supabase, id, {
        loginEmail: trainerRow?.login_email,
        firstName: trainerRow?.first_name,
        lastName: trainerRow?.last_name,
        tenantId: trainerRow?.tenant_id,
      }));
    if (linkedPlayerId) patch.linked_player_person_id = linkedPlayerId;

    const { data: existing, error: selErr } = await supabase
      .from("club_people")
      .select("id, contact_source, linked_player_person_id")
      .eq("id", id)
      .maybeSingle();
    if (selErr) return { ok: false, error: selErr };

    if (existing?.id) {
      if (
        existing.contact_source &&
        existing.contact_source !== CLUB_PERSON_CONTACT_SOURCE_TRAINER
      ) {
        return { ok: false, error: "contact-id-conflict-not-trainer" };
      }
      if (!patch.linked_player_person_id && existing.linked_player_person_id) {
        patch.linked_player_person_id = existing.linked_player_person_id;
      }
      const upd = await updateClubPerson(id, patch);
      return upd?.ok ? { ok: true } : { ok: false, error: upd?.error };
    }

    const insertPayload = {
      id,
      ...patch,
      created_by: trainerRow.created_by ?? null,
    };
    const ins = await createClubPerson(insertPayload, {
      skipNameDuplicateCheck: true,
    });
    return ins?.ok ? { ok: true } : { ok: false, error: ins?.error };
  } catch (e) {
    return { ok: false, error: e };
  }
}

/**
 * Spiegelt alle Trainer des aktuellen Mandanten (RLS) nach club_people — idempotent.
 * @returns {Promise<{ ok: boolean, synced?: number, error?: unknown }>}
 */
export async function syncAllTrainerMirrorsToClubPeople() {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("trainerstunden_trainers")
      .select(
        "id, tenant_id, created_by, first_name, last_name, login_email, notes, is_inactive, inactive_at, street, house_number, postal_code, city, country",
      );
    if (error) return { ok: false, error };

    let synced = 0;
    const rows = data || [];
    const batchSize = 5;
    for (let offset = 0; offset < rows.length; offset += batchSize) {
      const chunk = rows.slice(offset, offset + batchSize);
      const results = await Promise.all(
        chunk.map((row) => upsertClubPersonMirrorFromTrainer(row)),
      );
      synced += results.filter((r) => r?.ok).length;
    }
    return { ok: true, synced };
  } catch (e) {
    return { ok: false, error: e };
  }
}
